import { Fragment, useEffect, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Alert from "react-bootstrap/Alert";
import CertAuditReportTable from "../components/CertAuditReportTable";
import TimesheetWeekTable from "../components/TimesheetWeekTable";
import MissedAvailabilityReportTable from "../components/MissedAvailabilityReportTable";
import AvailabilityBookingsReportTable from "../components/AvailabilityBookingsReportTable";
import NurseDateLastWorkedReportTable from "../components/NurseDateLastWorkedReportTable";
import NurseActiveNotYetWorkedReportTable from "../components/NurseActiveNotYetWorkedReportTable";
import SageInvoiceReportTable from "../components/SageInvoiceReportTable";
import { PageTitleController } from "../components/PageTitleController";
import {
  BsCalendarCheck,
  BsClockHistory,
  BsDatabaseFill,
  BsFileLock,
  BsPersonCheck,
  BsPersonExclamation,
  BsReceipt,
} from "react-icons/bs";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isAdminRole } from "../util/Permissions";

export const Reporting = () => {
  let { state } = useLocation();
  const getKeyStartingState = () => {
    if (state === null || state === undefined) return "missed-availability";
    if (state.hasOwnProperty("redirectKey")) {
      // Only return redirectKey if it's valid based on permissions
      return (state.redirectKey === "timesheet-weeks" || state.redirectKey === "sage-invoices") &&
        !hasTimesheetPermission
        ? "missed-availability"
        : state.redirectKey;
    }
    return "missed-availability";
  };

  const { authAxios } = useAuth();
  const [userRole, setUserRole] = useState("");
  const [hasTimesheetPermission, setHasTimesheetPermission] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize state and fetch user data
  useEffect(() => {
    document.title = "Reporting";
    fetchCurrentUser();
  }, []);

  // Set the key after permissions are loaded
  const [key, setKey] = useState("");

  useEffect(() => {
    if (!loading) {
      setKey(getKeyStartingState());
    }
  }, [loading]);

  const fetchCurrentUser = async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {},
      };

      const response = await authAxios().get(
        "/api/backoffice/users/current_user",
        config,
      );

      const role = response?.data?.role || "";
      setUserRole(role);

      // Check if user has permission to view timesheet weeks
      // Only ADMIN and SUPERADMIN roles can view timesheet data
      const hasPermission = role === "ADMIN" || role === "SUPERADMIN";
      setHasTimesheetPermission(hasPermission);
    } catch (error) {
      console.error("Error fetching current user:", error);
      setHasTimesheetPermission(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab selection
  const handleTabSelect = (selectedKey) => {
    // If user tries to access timesheet tab without permission, show alert
    if (
      (selectedKey === "timesheet-weeks" || selectedKey === "sage-invoices") &&
      !hasTimesheetPermission
    ) {
      return; // Don't change tabs
    }
    setKey(selectedKey);
  };

  if (loading) {
    return (
      <Fragment>
        <PageTitleController
          title={"Reports"}
          icon={<BsDatabaseFill className={"icon-right-spacing"} />}
        />
        <div className="p-3">Loading report options...</div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageTitleController
        title={"Reports"}
        icon={<BsDatabaseFill className={"icon-right-spacing"} />}
      />

      <Tabs
        id="reports-table-tabs"
        activeKey={key}
        onSelect={handleTabSelect}
        className="mb-3"
      >
        <Tab
          eventKey="missed-availability"
          title={
            <span>
              <BsCalendarCheck className="me-2" />
              Missed Availability
            </span>
          }
        >
          <MissedAvailabilityReportTable />
        </Tab>

        <Tab
          eventKey="availability-bookings"
          title={
            <span>
              <BsPersonCheck className="me-2" />
              Availability Bookings
            </span>
          }
        >
          <AvailabilityBookingsReportTable />
        </Tab>

        <Tab
          eventKey="audit"
          title={
            <span>
              <BsDatabaseFill className="me-2" />
              Audit
            </span>
          }
        >
          <CertAuditReportTable />
        </Tab>

        <Tab
          eventKey="nurse-date-last-worked"
          title={
            <span>
              <BsClockHistory className="me-2" />
              Nurse Last Worked
            </span>
          }
        >
          <NurseDateLastWorkedReportTable />
        </Tab>

        <Tab
          eventKey="nurse-active-not-yet-worked"
          title={
            <span>
              <BsPersonExclamation className="me-2" />
              Nurse Issues
            </span>
          }
        >
          <NurseActiveNotYetWorkedReportTable />
        </Tab>

        {isAdminRole(userRole) && (
          <Tab
            eventKey="sage-invoices"
            title={
              <span>
                <BsReceipt className="me-2" />
                Sage Invoices
              </span>
            }
          >
            <SageInvoiceReportTable />
          </Tab>
        )}

        <Tab
          eventKey="timesheet-weeks"
          title={
            <span>
              Timesheet Weeks
              {!hasTimesheetPermission && <BsFileLock className="ms-2" />}
            </span>
          }
          disabled={!hasTimesheetPermission}
        >
          {hasTimesheetPermission ? (
            <TimesheetWeekTable />
          ) : (
            <Alert variant="warning" className="mt-3">
              <BsFileLock className="me-2" />
              You don't have permission to access timesheet data. Please contact
              an administrator if you need access.
            </Alert>
          )}
        </Tab>
      </Tabs>
    </Fragment>
  );
};
